import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useSession } from '../stores/session';

/**
 * Gate for routes that need a signed-in user.
 *
 * Two details that matter more than the redirect itself:
 *
 * **It relies on `ready`.** The session is restored asynchronously on load, so
 * for the first moment after a refresh there is no user *and no answer yet*.
 * Treating that as "signed out" would bounce a returning visitor to the login
 * screen every time they reload. `App` holds rendering until `ready`, so by the
 * time this runs the answer is real.
 *
 * **It remembers where you were going.** The location is passed through the
 * redirect so signing in returns you to the page you asked for rather than
 * dumping you on the home page - which is the difference between a deep link
 * being useful and being decorative.
 *
 * `replace` keeps the protected URL out of history, so the back button after
 * signing in does not land on a page that will just redirect again.
 */
export function RequireAuth({ verified = false }: { verified?: boolean }) {
  const user = useSession((s) => s.user);
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Distinct from "not signed in": there is a clear next step and a button that
  // takes it, so it gets its own page rather than a dead end (see RequireAuth
  // on the server, which draws the same distinction with `email_unverified`).
  if (verified && !user.email_verified) {
    return <Navigate to="/verify-email" replace />;
  }

  return <Outlet />;
}

/** The mirror image: keeps a signed-in user off the login and register pages. */
export function RequireAnonymous() {
  const user = useSession((s) => s.user);

  return user ? <Navigate to="/" replace /> : <Outlet />;
}
